import type { Schedule } from "../types";

interface WizardCountsCardProps {
  schedule: Schedule;
  onContinue: () => void;
}

interface DetailRow {
  label: string;
  value: number;
}

/**
 * Step 1 success card. Matches the prototype's "Calendar fetched" view
 * (see `Class Scheduler.dc.html` lines 238–258).
 */
export function WizardCountsCard({
  schedule,
  onContinue,
}: WizardCountsCardProps) {
  const counts = schedule.counts;
  if (!counts) {
    // Should never happen — create/refresh always return counts.
    return null;
  }

  const totalEvents = counts.courseEvents + counts.ignoredEvents;
  const detailRows: DetailRow[] = [
    { label: "Distinct course IDs", value: counts.distinctCourseIds },
    { label: "Distinct rooms", value: counts.distinctRooms },
    { label: "Distinct teachers", value: counts.distinctTeachers },
    { label: "Distinct subject areas", value: counts.distinctSubjectAreas },
    { label: "Rooms discovered", value: counts.roomsDiscovered },
  ];

  return (
    <div
      data-testid="wizard-counts-card"
      className="bg-surface border border-border-subtle rounded-lg p-6 animate-fade-in"
    >
      <div className="flex items-center gap-2.5 mb-[18px]">
        <div className="w-8 h-8 rounded-full bg-green-900/70 flex items-center justify-center text-green-400">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </div>
        <div>
          <h2 className="text-[17px] font-semibold text-white">
            Calendar fetched
          </h2>
          <p
            className="text-[13px] text-text-muted"
            data-testid="counts-subline"
          >
            {totalEvents} {totalEvents === 1 ? "event" : "events"} across 1
            calendar
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-[18px]">
        <div className="bg-page border border-border-subtle rounded-md px-3.5 py-3.5">
          <div
            className="text-2xl font-bold text-green-400"
            data-testid="counts-course-events"
          >
            {counts.courseEvents}
          </div>
          <div className="text-xs text-text-muted">course events detected</div>
        </div>
        <div className="bg-page border border-border-subtle rounded-md px-3.5 py-3.5">
          <div
            className="text-2xl font-bold text-text-muted"
            data-testid="counts-ignored-events"
          >
            {counts.ignoredEvents}
          </div>
          <div className="text-xs text-text-muted">
            non-class events ignored
          </div>
        </div>
      </div>

      <div className="bg-page border border-border-subtle rounded-md px-3.5 py-3 mb-[22px] text-[13px] text-text-soft">
        {detailRows.map((row) => (
          <div
            key={row.label}
            className="flex justify-between py-[3px]"
            data-testid={`counts-detail-${row.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className="text-text-muted">{row.label}</span>
            <span>{row.value}</span>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onContinue}
          data-testid="continue-to-schedule"
          className="bg-brand hover:bg-brand-hover text-white border-none rounded-md px-[18px] py-[9px] font-semibold text-sm cursor-pointer"
        >
          Continue to schedule
        </button>
      </div>
    </div>
  );
}
